import { CommonModule } from '@angular/common';
import { Component } from '@angular/core';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { AuthenticationService } from '../services/authentication.service';
import { AppService } from '../services/app.service';
import { ToastrService } from 'ngx-toastr';
import { clearTempPasswordExpiration } from '../helpers/temp-password.util';

@Component({
  selector: 'app-reset-password',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, RouterModule],
  templateUrl: './reset-password.component.html',
  styleUrl: './reset-password.component.scss',
})
export class ResetPasswordComponent {
  email: string = '';
  isSubmitting = false;
  passwordRegx: RegExp = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[\W_]).{8,}$/;
  showTempPassword: boolean = false;
  showPassword: boolean = false;
  showConfirmPassword: boolean = false;

  constructor(
    public router: Router,
    private authService: AuthenticationService,
    private appService: AppService,
    private toastr: ToastrService,
  ) {
    this.email = localStorage.getItem('email') ?? '';
  }

  protected resetPassword = new FormGroup(
    {
      tempPassword: new FormControl('', [Validators.required]),
      password: new FormControl('', [
        Validators.required,
        Validators.pattern(this.passwordRegx),
      ]),
      confirmPassword: new FormControl('', [
        Validators.required,
        Validators.pattern(this.passwordRegx),
      ]),
    },
    { validators: this.customPasswordMatching.bind(this) },
  );

  public customPasswordMatching(
    control: AbstractControl,
  ): ValidationErrors | null {
    const password = control.get('password')?.value;
    const confirmPassword = control.get('confirmPassword')?.value;

    return password === confirmPassword
      ? null
      : { passwordMismatchError: true };
  }

  toggleTempPasswordVisibility() {
    this.showTempPassword = !this.showTempPassword;
  }

  togglePasswordVisibility() {
    this.showPassword = !this.showPassword;
  }

  toggleConfirmPasswordVisibility() {
    this.showConfirmPassword = !this.showConfirmPassword;
  }

  trimTempPassword() {
    let tempPassword = this.resetPassword.get('tempPassword')?.value;
    if (tempPassword) {
      this.resetPassword.patchValue({
        tempPassword: tempPassword.trim(),
      });
    }
  }

  trimPassword() {
    let password = this.resetPassword.get('password')?.value;
    if (password) {
      this.resetPassword.patchValue({
        password: password.trim(),
      });
    }
  }

  trimConfirmPassword() {
    let password = this.resetPassword.get('confirmPassword')?.value;
    if (password) {
      this.resetPassword.patchValue({
        confirmPassword: password.trim(),
      });
    }
  }

  setPassword() {
    if (this.resetPassword.invalid) {
      this.resetPassword.markAllAsTouched();
      return;
    }
    if (this.resetPassword.valid) {
      this.isSubmitting = true;
      this.authService
        .resetPassword(
          this.email,
          this.resetPassword.value.tempPassword ?? '',
          this.resetPassword.value.password ?? ''
        )
        .subscribe({
          next: (data) => {
            this.isSubmitting = false;
            // CLEAR rather than record: once a reset has been attempted there
            // is no temporary password left for an expiration to describe.
            // `setNewPassword` clears `forceReset` server-side but leaves
            // `tempPasswordExpiration` on the user, so this response echoes the
            // OLD, already-past timestamp — storing it would leave the login
            // page refusing any non-complex password ("Your temporary password
            // has expired.") without even attempting a sign-in. Kept outside
            // the SUCCESS branch so a failed reset can't leave a stale gate
            // behind either.
            clearTempPasswordExpiration();
            if (data.status === 'SUCCESS') {
              this.appService.setMessage(data.message);
              this.router.navigate(['login']);
            } else {
              this.toastr.error(data.message);
            }
          },
          error: () => {
            this.isSubmitting = false;
            this.toastr.error('An error occurred. Please try again.');
          }
        });
    }
  }

  cancel() {
    this.router.navigate(['login']);
  }
}
